/**
 * BottomMenu.jsx
 * Premium bottom navigation for mobile devices
 *
 * Features:
 * - 5 main navigation items: Home, Reports, Map, Settings, Account
 * - Safe area insets support for notched devices
 * - Connection status indicator
 * - Glassmorphism design
 * - Touch-friendly targets (min 44px)
 */

import { useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { usePremiumTokens } from '../theme/usePremiumTokens';

// Icons
import GridViewIcon from '@mui/icons-material/GridView';
import DescriptionIcon from '@mui/icons-material/Description';
import MapIcon from '@mui/icons-material/Map';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import TuneIcon from '@mui/icons-material/Tune';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BuildIcon from '@mui/icons-material/Build';
import FolderIcon from '@mui/icons-material/Folder';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import InsightsIcon from '@mui/icons-material/Insights';
import SmartToyIcon from '@mui/icons-material/SmartToy';

import { sessionActions } from '../../store';
import { useTranslation } from './LocalizationProvider';
import { useRestriction } from '../util/permissions';
import { nativePostMessage } from './NativeInterface';

// Constants
const BOTTOM_MENU_HEIGHT = 64;
const SAFE_AREA_BOTTOM = 'env(safe-area-inset-bottom, 0px)';

const BottomMenu = () => {
  const tokens = usePremiumTokens();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const t = useTranslation();

  // Only show on mobile
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Permissions
  const readonly = useRestriction('readonly');

  // Redux state
  const devices = useSelector((state) => state.devices.items, shallowEqual);
  const user = useSelector((state) => state.session.user);
  const socket = useSelector((state) => state.session.socket);
  const selectedDeviceId = useSelector((state) => state.devices.selectedId);

  // Menu anchors
  const [routesAnchor, setRoutesAnchor] = useState(null);
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [accountAnchor, setAccountAnchor] = useState(null);

  // Safe tokens
  const safeTokens = useMemo(() => ({
    surface: {
      elevated: tokens?.surface?.elevated || '#161b22',
      glass: tokens?.surface?.glass || 'rgba(22, 27, 34, 0.85)',
    },
    border: {
      subtle: tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)',
    },
    text: {
      primary: tokens?.text?.primary || '#f0f6fc',
      secondary: tokens?.text?.secondary || '#8b949e',
      muted: tokens?.text?.muted || '#6e7681',
    },
    accent: {
      primary: tokens?.accent?.primary || '#00d4aa',
      subtle: tokens?.accent?.subtle || 'rgba(0, 212, 170, 0.1)',
      glow: tokens?.accent?.glow || 'rgba(0, 212, 170, 0.3)',
    },
    status: {
      alarm: tokens?.status?.alarm || '#f85149',
    },
    radius: {
      md: tokens?.radius?.md || '8px',
      lg: tokens?.radius?.lg || '12px',
    },
  }), [tokens]);

  // Current active section
  // 2ECOMP: mobile — separar '/' (map/MainPage) de '/overview' (home)
  const currentSection = useMemo(() => {
    const path = location.pathname;
    if (path === '/overview') return 'home';
    if (path === '/') return 'map';
    // Routing routes
    if (path.includes('route-planning') || path.includes('route-execution') ||
        path.includes('operations-center') || path.includes('routing-dashboard') ||
        path.includes('driver-activity') || path === '/settings/routes' ||
        path.startsWith('/driver')) return 'routes';
    if (path.startsWith('/reports') || path.startsWith('/replay') || path.startsWith('/heatmap') || path.startsWith('/eventmap')) return 'reports';
    if (path === '/geofences' || path === '/fleet' || path === '/temperature') return 'map';
    if (path.startsWith('/settings')) return 'settings';
    return null;
  }, [location.pathname]);

  // 2ECOMP: mobile — toggle bottom sheet via custom event (quando já na MainPage)
  const handleMapClick = useCallback(() => {
    if (currentSection === 'map') {
      window.dispatchEvent(new CustomEvent('mobileToggleDeviceSheet'));
    } else {
      navigate('/');
    }
  }, [currentSection, navigate]);

  // Navigation handlers
  const handleNavigation = (section) => {
    switch (section) {
      case 'home':
        navigate('/overview');
        break;
      case 'reports': {
        let id = selectedDeviceId;
        if (!id) {
          const deviceIds = Object.keys(devices);
          if (deviceIds.length === 1) {
            [id] = deviceIds;
          }
        }
        navigate(id ? `/reports/combined?deviceId=${id}` : '/reports/combined');
        break;
      }
      case 'map':
        navigate('/');
        break;
      case 'settings':
        setSettingsAnchor(null);
        navigate('/settings/devices');
        break;
      case 'account':
        setAccountAnchor(null);
        navigate(`/settings/user/${user?.id}`);
        break;
      default:
        break;
    }
  };

  // Logout handler
  const handleLogout = async () => {
    setAccountAnchor(null);

    const notificationToken = window.localStorage.getItem('notificationToken');
    if (notificationToken && !user?.readonly) {
      window.localStorage.removeItem('notificationToken');
      const userTokens = user?.attributes?.notificationTokens?.split(',') || [];
      if (userTokens.includes(notificationToken)) {
        const updatedUser = {
          ...user,
          attributes: {
            ...user.attributes,
            notificationTokens: userTokens.length > 1
              ? userTokens.filter((it) => it !== notificationToken).join(',')
              : undefined,
          },
        };
        await fetch(`/api/users/${user.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedUser),
        });
      }
    }

    await fetch('/api/session', { method: 'DELETE' });
    nativePostMessage('logout');
    navigate('/login');
    dispatch(sessionActions.updateUser(null));
  };

  // Menu items for routes popup
  const routesMenuItems = [
    { icon: <AltRouteIcon />, label: t('routePlanning'), path: '/settings/route-planning' },
    { icon: <PlayCircleFilledIcon />, label: t('routeExecution') || 'Execução de Rotas', path: '/settings/route-execution' },
    { icon: <GridViewIcon />, label: t('operationsCenter'), path: '/settings/operations-center' },
    { icon: <InsightsIcon />, label: t('routingDashboard'), path: '/settings/routing-dashboard' },
    { icon: <SmartToyIcon />, label: t('driverApp'), path: '/driver' },
  ];

  // Menu items for settings popup
  const settingsMenuItems = [
    { icon: <DirectionsCarIcon />, label: t('deviceTitle'), path: '/settings/devices' },
    { icon: <NotificationsIcon />, label: t('sharedNotifications'), path: '/settings/notifications' },
    { icon: <FolderIcon />, label: t('settingsGroups'), path: '/settings/groups' },
    { icon: <BuildIcon />, label: t('sharedMaintenance'), path: '/settings/maintenances' },
    { icon: <TuneIcon />, label: t('sharedPreferences'), path: '/settings/preferences' },
    { icon: <DescriptionIcon />, label: t('reportTitle'), path: '/reports/combined' },
  ];

  // Don't render on desktop
  if (!isMobile) return null;

  // Navigation item component
  const NavItem = ({ icon, label, active, onClick, badge }) => (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        minWidth: 56,
        minHeight: 56,
        py: 0.5,
        cursor: 'pointer',
        color: active ? safeTokens.accent.primary : safeTokens.text.muted,
        transition: 'all 0.2s ease',
        borderRadius: safeTokens.radius.md,
        mx: 0.25,
        '&:hover': {
          backgroundColor: safeTokens.accent.subtle,
          color: active ? safeTokens.accent.primary : safeTokens.text.secondary,
        },
        '&:active': {
          transform: 'scale(0.95)',
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        {badge ? (
          <Badge
            color="error"
            variant="dot"
            overlap="circular"
            invisible={badge.invisible}
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: safeTokens.status.alarm,
                boxShadow: `0 0 6px ${safeTokens.status.alarm}60`,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                '& .MuiSvgIcon-root': {
                  fontSize: 24,
                  transition: 'all 0.2s ease',
                  filter: active ? `drop-shadow(0 0 8px ${safeTokens.accent.glow})` : 'none',
                },
              }}
            >
              {icon}
            </Box>
          </Badge>
        ) : (
          <Box
            sx={{
              display: 'flex',
              '& .MuiSvgIcon-root': {
                fontSize: 24,
                transition: 'all 0.2s ease',
                filter: active ? `drop-shadow(0 0 8px ${safeTokens.accent.glow})` : 'none',
              },
            }}
          >
            {icon}
          </Box>
        )}
      </Box>
      <Typography
        sx={{
          fontSize: '0.625rem',
          fontWeight: active ? 600 : 500,
          mt: 0.25,
          letterSpacing: '0.02em',
          transition: 'all 0.2s ease',
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          backgroundColor: safeTokens.surface.glass,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderTop: `1px solid ${safeTokens.border.subtle}`,
          boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.3)',
          // Safe area support
          paddingBottom: SAFE_AREA_BOTTOM,
          height: `calc(${BOTTOM_MENU_HEIGHT}px + ${SAFE_AREA_BOTTOM})`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            height: BOTTOM_MENU_HEIGHT,
            px: 0.5,
          }}
        >
          {/* Home */}
          <NavItem
            icon={<GridViewIcon />}
            label="Home"
            active={currentSection === 'home'}
            onClick={() => handleNavigation('home')}
            badge={{ invisible: socket !== false }}
          />

          {/* Routes */}
          {!readonly && (
            <NavItem
              icon={<AltRouteIcon />}
              label={t('routePlanning')}
              active={currentSection === 'routes'}
              onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setRoutesAnchor({ top: rect.top, left: rect.left + rect.width / 2 });
            }}
            />
          )}

          {/* Map / Devices toggle */}
          <NavItem
            icon={<MapIcon />}
            label={t('mapTitle')}
            active={currentSection === 'map'}
            onClick={handleMapClick}
          />

          {/* Settings */}
          {!readonly && (
            <NavItem
              icon={<SettingsIcon />}
              label={t('settingsTitle')}
              active={currentSection === 'settings'}
              onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setSettingsAnchor({ top: rect.top, left: rect.left + rect.width / 2 });
            }}
            />
          )}

          {/* Account */}
          <NavItem
            icon={<PersonIcon />}
            label={t('settingsUser')}
            active={false}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setAccountAnchor({ top: rect.top, left: rect.left + rect.width / 2 });
            }}
          />
        </Box>
      </Paper>

      {/* Routes Menu */}
      <Menu
        anchorReference="anchorPosition"
        anchorPosition={routesAnchor || undefined}
        open={Boolean(routesAnchor)}
        onClose={() => setRoutesAnchor(null)}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{
          sx: {
            backgroundColor: safeTokens.surface.elevated,
            backdropFilter: 'blur(20px)',
            borderRadius: safeTokens.radius.lg,
            border: `1px solid ${safeTokens.border.subtle}`,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            minWidth: 200,
            mb: 1,
          },
        }}
      >
        {routesMenuItems.map((item) => (
          <MenuItem
            key={item.path}
            onClick={() => {
              setRoutesAnchor(null);
              navigate(item.path);
            }}
            sx={{
              py: 1.5,
              '&:hover': {
                backgroundColor: safeTokens.accent.subtle,
              },
            }}
          >
            <ListItemIcon sx={{ color: safeTokens.text.secondary, minWidth: 36 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                color: safeTokens.text.primary,
              }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* Settings Menu */}
      <Menu
        anchorReference="anchorPosition"
        anchorPosition={settingsAnchor || undefined}
        open={Boolean(settingsAnchor)}
        onClose={() => setSettingsAnchor(null)}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{
          sx: {
            backgroundColor: safeTokens.surface.elevated,
            backdropFilter: 'blur(20px)',
            borderRadius: safeTokens.radius.lg,
            border: `1px solid ${safeTokens.border.subtle}`,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            minWidth: 200,
            mb: 1,
          },
        }}
      >
        {settingsMenuItems.map((item) => (
          <MenuItem
            key={item.path}
            onClick={() => {
              setSettingsAnchor(null);
              navigate(item.path);
            }}
            sx={{
              py: 1.5,
              '&:hover': {
                backgroundColor: safeTokens.accent.subtle,
              },
            }}
          >
            <ListItemIcon sx={{ color: safeTokens.text.secondary, minWidth: 36 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                color: safeTokens.text.primary,
              }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* Account Menu */}
      <Menu
        anchorReference="anchorPosition"
        anchorPosition={accountAnchor || undefined}
        open={Boolean(accountAnchor)}
        onClose={() => setAccountAnchor(null)}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{
          sx: {
            backgroundColor: safeTokens.surface.elevated,
            backdropFilter: 'blur(20px)',
            borderRadius: safeTokens.radius.lg,
            border: `1px solid ${safeTokens.border.subtle}`,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            minWidth: 180,
            mb: 1,
          },
        }}
      >
        {/* User info header */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${safeTokens.border.subtle}` }}>
          <Typography sx={{ color: safeTokens.text.primary, fontWeight: 600, fontSize: '0.875rem' }}>
            {user?.name || user?.email}
          </Typography>
          <Typography sx={{ color: safeTokens.text.muted, fontSize: '0.75rem' }}>
            {user?.email}
          </Typography>
        </Box>

        <MenuItem
          onClick={() => handleNavigation('account')}
          sx={{
            py: 1.5,
            '&:hover': { backgroundColor: safeTokens.accent.subtle },
          }}
        >
          <ListItemIcon sx={{ color: safeTokens.text.secondary, minWidth: 36 }}>
            <PersonIcon />
          </ListItemIcon>
          <ListItemText
            primary={t('settingsUser')}
            primaryTypographyProps={{ fontSize: '0.875rem', color: safeTokens.text.primary }}
          />
        </MenuItem>

        <Divider sx={{ borderColor: safeTokens.border.subtle, my: 0.5 }} />

        <MenuItem
          onClick={handleLogout}
          sx={{
            py: 1.5,
            '&:hover': { backgroundColor: `${safeTokens.status.alarm}15` },
          }}
        >
          <ListItemIcon sx={{ color: safeTokens.status.alarm, minWidth: 36 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary={t('loginLogout')}
            primaryTypographyProps={{ fontSize: '0.875rem', color: safeTokens.status.alarm }}
          />
        </MenuItem>
      </Menu>

      {/* Spacer to prevent content from hiding behind bottom menu */}
      <Box
        sx={{
          height: `calc(${BOTTOM_MENU_HEIGHT}px + ${SAFE_AREA_BOTTOM})`,
          flexShrink: 0,
        }}
      />
    </>
  );
};

export { BOTTOM_MENU_HEIGHT };
export default BottomMenu;
